// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { GameDefinition, RoundSummary, TourGameScore, TourRoundGameScore } from './types';
import { getGameDisplayName } from '@/features/game-definitions/i18n';

export const PLAYER_COL_WIDTH = 100;
export const GAME_COL_WIDTH = 48;
export const TOTAL_COL_WIDTH = 72;

export const buildGameNameLookup = (gameDefs: GameDefinition[], fallback: string) =>
  new Map(gameDefs.map((game) => [game.id, getGameDisplayName(game, fallback)]));

export const buildPlayerRows = (
  scoreboard: TourGameScore[],
  nameLookup: Map<string, string>,
  fallback: string,
  playerIds?: string[]
) => {
  const scoreByPlayer = new Map<string, { perGame: Map<string, number>; total: number }>();
  scoreboard.forEach((entry) => {
    if (!entry.playerId) return;
    const existing = scoreByPlayer.get(entry.playerId);
    if (!existing) {
      scoreByPlayer.set(entry.playerId, {
        perGame: new Map([[entry.gameId, entry.scorePoints]]),
        total: entry.scorePoints,
      });
      return;
    }

    existing.perGame.set(entry.gameId, entry.scorePoints);
    existing.total += entry.scorePoints;
  });

  // Derive the roster from the aggregated players so entries dropped above (no player id)
  // cannot come back as phantom zero-point rows.
  const effectivePlayerIds =
    playerIds && playerIds.length > 0 ? playerIds : Array.from(scoreByPlayer.keys());

  return effectivePlayerIds
    .map((playerId) => {
      const score = scoreByPlayer.get(playerId);
      return {
        playerId,
        name: nameLookup.get(playerId) ?? fallback,
        perGame: score?.perGame ?? new Map<string, number>(),
        total: score?.total ?? 0,
      };
    })
    .sort((a, b) => {
      if (b.total !== a.total) {
        return b.total - a.total;
      }
      return a.name.localeCompare(b.name);
    });
};

// Sort key for ordering a tour's rounds by playing date (earliest first),
// falling back to creation time when a play date is missing.
const roundOrderTime = (round: RoundSummary) => {
  const candidate = round.playDate ?? round.createdAt;
  if (!candidate) return 0;
  const parsed = Date.parse(candidate);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const buildOrderedRounds = (rounds: RoundSummary[]) =>
  rounds.slice().sort((a, b) => roundOrderTime(a) - roundOrderTime(b));

export const buildRoundScoreMap = (roundScoreboard: TourRoundGameScore[]) => {
  const roundGameMap = new Map<string, number>();
  roundScoreboard.forEach((score) => {
    if (!score.roundId || !score.gameId || !score.playerId) return;
    const key = `${score.playerId}:${score.gameId}:${score.roundId}`;
    roundGameMap.set(key, score.scorePoints);
  });
  return roundGameMap;
};

export const buildPerGamePerRoundRows = (
  playerIds: string[],
  orderedGameIds: string[],
  gameRoundIds: string[],
  roundGameMap: Map<string, number>,
  nameLookup: Map<string, string>,
  fallback: string
) =>
  playerIds
    .map((playerId) => {
      let total = 0;
      const pointsByGameRound = new Map<string, number>();
      orderedGameIds.forEach((gameId) => {
        gameRoundIds.forEach((roundId) => {
          const key = `${playerId}:${gameId}:${roundId}`;
          const points = roundGameMap.get(key) ?? 0;
          pointsByGameRound.set(key, points);
          total += points;
        });
      });
      return {
        playerId,
        name: nameLookup.get(playerId) ?? fallback,
        pointsByGameRound,
        total,
      };
    })
    .sort((a, b) => b.total - a.total);
